import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ReturnItemParams,
  subtitleWithoutDefault,
  variantsWithoutDefault,
} from "@redotech/redo-customer-portal-shared/util";
import { getItemDetailPrice } from "@redotech/redo-customer-portal-shared/util/get-item-detail-price";
import { shouldHideQuantityForBulkReturns } from "@redotech/redo-return-operations-common/draft-return/bulk-returns";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";

interface ItemDetailInfo {
  formattedItemValue: string;
  formattedPrice?: string;
  texts: {
    title: string;
    subtitle?: string;
    textStrings: string[];
    discountExplanation?: string;
    badgeText?: string;
    bonusText?: string;
    merchantCreatedPreRejectMessage?: string;
  };
  loading: boolean;
  imageUri: string;
  bonusBadge?: { text: string; color: PillTheme };
  returnTypeBadges?: { text: string; color: PillTheme }[];
  isTnplContractLineItem?: boolean;
}

export function getItemDetail(
  {
    item,
    isVertical = false,
    bonus,
    hideQuantity = false,
    hidePrice = false,
    settings,
  }: {
    item?: ReturnItemParams;
    isVertical?: boolean;
    bonus?: { value: number; currency: Currency };
    hideQuantity?: boolean;
    hidePrice?: boolean;
    settings: CustomerPortalAppSettings | undefined;
  },
  t: (key: string, vars?: Record<string, unknown>) => string,
  locale: string,
): ItemDetailInfo {
  const image = item?.imageSrc;

  const shouldHideQuantity = hideQuantity
    ? true
    : item && settings
      ? shouldHideQuantityForBulkReturns(
          item.collections,
          settings?.bulkReturnsEnabled,
          settings?.bulkReturns?.collections,
          settings?.bulkReturns?.hideQuantity,
        )
      : false;

  const shouldCombinePrice =
    item && settings
      ? shouldHideQuantityForBulkReturns(
          item.collections,
          settings?.bulkReturnsEnabled,
          settings?.bulkReturns?.collections,
          settings?.bulkReturns?.hideQuantity,
        )
      : false;

  const { formattedPrice, formattedItemValue, discountFormatted } =
    getItemDetailPrice(item, shouldCombinePrice, undefined, locale);

  if (!item) {
    return {
      imageUri: "",
      loading: true,
      formattedItemValue: "0",
      formattedPrice: "0",
      texts: { title: "", textStrings: [] },
    };
  }

  const { quantity, variants } = item;
  const retainedVariants = variantsWithoutDefault(variants);
  const isTnplContractLineItem = !!item.isTnplContractLineItem;

  const horizontalQuantityMessage =
    !shouldHideQuantity && quantity > 1
      ? t("Qty: {{quantity}} / ", { quantity })
      : "";
  const variantsString = retainedVariants.map((v) => v.value).join(" / ");
  // When prices are hidden (warranty flows), the horizontal layout drops the
  // item value from its combined text string and joins the remaining segments
  // so no dangling separators are left behind.
  const horizontalTextString = hidePrice
    ? [
        !shouldHideQuantity && quantity > 1
          ? t("Qty: {{quantity}}", { quantity })
          : "",
        variantsString,
      ]
        .filter((segment) => segment !== "")
        .join(" / ")
    : `${horizontalQuantityMessage}${formattedItemValue}${retainedVariants.length > 0 ? " / " : ""}${variantsString}`;
  const textStrings: string[] = [];
  if (isVertical) {
    textStrings.push(...retainedVariants.map((v) => `${v.name}: ${v.value}`));
    if (!shouldHideQuantity && quantity > 1) {
      textStrings.push(t("Quantity: {{quantity}}", { quantity }));
    }
  } else {
    textStrings.push(horizontalTextString);
  }

  return {
    loading: false,
    texts: {
      title: item.title,
      subtitle: subtitleWithoutDefault(item.subtitle),
      textStrings,
      discountExplanation: discountFormatted
        ? t("Your discount of {{discountFormatted}} has been reapplied.", {
            discountFormatted,
          })
        : undefined,
      badgeText: item.badgeText,
      merchantCreatedPreRejectMessage: t(
        "This item would have been rejected by your flow.",
      ),
    },
    formattedItemValue,
    formattedPrice,
    imageUri: image ?? "",
    bonusBadge: bonus
      ? {
          text:
            bonus.value > 0
              ? t("+ {{formattedAmount}} bonus", {
                  formattedAmount: getCurrencyFormatter(
                    bonus.currency,
                    undefined,
                    locale,
                  ).format(bonus.value),
                })
              : t("+ {{formattedAmount}} fee", {
                  formattedAmount: getCurrencyFormatter(
                    bonus.currency,
                    undefined,
                    locale,
                  ).format(bonus.value),
                }),
          color: bonus.value > 0 ? PillTheme.SUCCESS : PillTheme.DANGER,
        }
      : undefined,
    returnTypeBadges: item.returnTypeBadges,
    isTnplContractLineItem,
  };
}
